import { useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { fetchReactivationDetails } from 'src/services/reactivation.service';
import { fetchMockReactivationDetails } from 'src/services/mocks/reactivation.mock';
import {
  ReactivationDetails,
  ReactivationScenario,
  ReactivationSubscriptionDetails,
} from 'src/services/reactivation.types';
import {
  setReactivationDetails,
  setReactivationLoading,
  setReactivationError,
  clearReactivationState,
} from 'src/store/reactivation';
import { PaymentMethod } from '@stripe/stripe-js';
import { getCampaignFromSession } from 'src/util/campaign.util';
import AuthService from 'src/lib/auth/auth-service';
import { unauthenticate } from 'src/lib/auth/actions';
import history from 'src/lib/history';

interface UseReactivationDetailsReturn {
  scenario: ReactivationScenario | null;
  subscription: ReactivationSubscriptionDetails | null;
  paymentMethod: PaymentMethod | null;
  shouldOnlyReactivateSubscription: boolean;
  isLoading: boolean;
  error: string | null;
  refetchReactivationDetails: () => void;
}

/** Set to true to test with mock data locally; set to false to call real API */
const USE_MOCK_DATA = false;

export const useReactivationDetails = (): UseReactivationDetailsReturn => {
  const dispatch = useAppDispatch();
  const {
    scenario,
    subscription,
    paymentMethod,
    shouldOnlyReactivateSubscription,
    isLoading,
    error,
  } = useAppSelector(state => state.reactivation);
  const hasFetchedRef = useRef(false);

  const loadReactivationDetails = useCallback(async () => {
    dispatch(setReactivationLoading(true));
    try {
      // Pass the campaign code from session — optional, non-blocking.
      const campaignCode = getCampaignFromSession();
      const data: ReactivationDetails = USE_MOCK_DATA
        ? await fetchMockReactivationDetails(campaignCode)
        : await fetchReactivationDetails(campaignCode);
      dispatch(setReactivationDetails(data));
    } catch (err) {
      console.error('Error fetching reactivation details:', err);
      const statusCode = (err as any)?.response?.status || (err as any)?.status;
      if (statusCode === 401 || statusCode === 403) {
        AuthService.logOut();
        dispatch(unauthenticate());
        dispatch(clearReactivationState());
        history.replace('/login-account');
        return;
      }
      dispatch(setReactivationError('Failed to fetch reactivation details'));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      loadReactivationDetails();
    }
  }, [loadReactivationDetails]);

  return {
    scenario,
    subscription,
    paymentMethod,
    shouldOnlyReactivateSubscription,
    isLoading,
    error,
    refetchReactivationDetails: loadReactivationDetails,
  };
};
